import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import * as XLSX from 'xlsx';
import { LambdaService } from 'src/app/lambda.service';
import { ThemingService } from 'src/app/theming.service';
import {
  ReportCatalogOption,
  SalesReportExcelRow,
  SalesReportFilters,
  SalesReportItem,
  SalesReportPreviewRow,
  SalesReportResponse,
  SalesReportSale,
  SalesReportSummary,
  ValidatedSalesReportExcelRow,
} from './reports.models';
import {
  extractShopCompanies,
  ShopCompany,
} from 'src/app/components/shop-companies/shop-company.models';

type ExcelCell = XLSX.CellObject & { z?: string };

@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss'],
})
export class ReportsComponent implements OnInit {
  readonly pageSizeOptions = [10, 20, 50];
  readonly saleStateOptions: ReportCatalogOption[] = [
    { id: -1, name: 'Todos' },
    { id: 0, name: 'Cancelado' },
    { id: 1, name: 'Pendiente de verificación' },
    { id: 2, name: 'Preparando orden' },
    { id: 3, name: 'Entregado' },
    { id: 4, name: 'Pendiente Compra Click' },
    { id: 5, name: 'Completado' },
    { id: 6, name: 'Parcial' },
  ];
  readonly excelHeaders = [
    'Identificación',
    'Nombre',
    'Documento',
    'Fecha',
    'Monto Principal',
    'Monto Descuento',
    'Monto Neto',
    'Plazo Meses',
    'Fecha Primer Cuota',
    'Cuota Mensual',
    'Observaciones',
  ];

  filtersForm: FormGroup;
  isLoading = false;
  isExporting = false;
  hasSearched = false;
  showAdditionalFilters = false;
  errorMessage = '';
  reportResponse: SalesReportResponse | null = null;
  summary: SalesReportSummary = {};
  previewRows: SalesReportPreviewRow[] = [];
  validExcelRows: ValidatedSalesReportExcelRow[] = [];
  invalidExcelRows: SalesReportExcelRow[] = [];
  paymentTypes: ReportCatalogOption[] = [];
  shopPoints: ReportCatalogOption[] = [];
  shopCompanies: ReportCatalogOption[] = [];
  currentPage = 1;
  pageSize = 10;

  constructor(
    private fb: FormBuilder,
    private lambdaService: LambdaService,
    public themingService: ThemingService
  ) {
    this.filtersForm = this.fb.group(
      {
        startDate: [''],
        endDate: [''],
        minAmount: ['', [this.nonNegativeNumberValidator()]],
        maxAmount: ['', [this.nonNegativeNumberValidator()]],
        searchString: [''],
        saleState: [-1],
        paymentTypeId: [''],
        shopPointId: [''],
        shopCompanyId: [''],
        paymentTypeMode: [''],
        productPaymentMode: [''],
      },
      { validators: [this.dateRangeValidator(), this.amountRangeValidator()] }
    );
  }

  ngOnInit(): void {
    this.loadCatalogs();
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.previewRows.length / this.pageSize));
  }

  get pagedSales(): SalesReportPreviewRow[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.previewRows.slice(start, start + this.pageSize);
  }

  get canDownloadExcel(): boolean {
    return !this.isLoading && !this.isExporting && this.validExcelRows.length > 0;
  }

  async loadCatalogs(): Promise<void> {
    await Promise.all([
      this.loadPaymentTypes(),
      this.loadShopPoints(),
      this.loadShopCompanies(),
    ]);
  }

  async loadReport(): Promise<void> {
    this.errorMessage = '';
    this.filtersForm.markAllAsTouched();

    if (!this.themingService.shopId) {
      this.errorMessage = 'REPORT_SHOP_REQUIRED';
      return;
    }

    if (this.filtersForm.invalid || this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.hasSearched = true;
    this.clearReportData();

    try {
      const filters = this.buildFilters();
      const response = await this.lambdaService.getSalesReport(filters);
      this.reportResponse = this.normalizeResponse(response);
      this.summary = this.reportResponse.summary;
      this.validateExcelRows(this.reportResponse.excelRows);
      this.previewRows = this.reportResponse.sales.map((sale) => this.toPreviewRow(sale));
      this.currentPage = 1;
    } catch (error) {
      console.error('Error loading sales report:', error);
      this.clearReportData();
      this.errorMessage = 'REPORT_LOAD_ERROR';
    } finally {
      this.isLoading = false;
    }
  }

  clearFilters(): void {
    this.filtersForm.reset({
      startDate: '',
      endDate: '',
      minAmount: '',
      maxAmount: '',
      searchString: '',
      saleState: -1,
      paymentTypeId: '',
      shopPointId: '',
      shopCompanyId: '',
      paymentTypeMode: '',
      productPaymentMode: '',
    });
    this.errorMessage = '';
    this.hasSearched = false;
    this.currentPage = 1;
    this.clearReportData();
  }

  changePage(page: number): void {
    if (page < 1 || page > this.totalPages) {
      return;
    }
    this.currentPage = page;
  }

  onPageSizeChange(value: string): void {
    const nextSize = Number(value);
    this.pageSize = this.pageSizeOptions.includes(nextSize) ? nextSize : 10;
    this.currentPage = 1;
  }

  async downloadExcel(): Promise<void> {
    if (!this.canDownloadExcel) {
      return;
    }

    this.isExporting = true;
    try {
      const workbook = this.buildWorkbook(this.validExcelRows);
      XLSX.writeFile(workbook, this.buildFileName());
    } catch (error) {
      console.error('Error generating sales report Excel:', error);
      this.errorMessage = 'REPORT_EXPORT_ERROR';
    } finally {
      this.isExporting = false;
    }
  }

  buildFilters(): SalesReportFilters {
    const raw = this.filtersForm.getRawValue();
    const filters: SalesReportFilters = {
      shopId: this.themingService.shopId,
    };

    this.appendFilter(filters, 'startDate', raw.startDate);
    this.appendFilter(filters, 'endDate', raw.endDate);
    this.appendFilter(filters, 'minAmount', raw.minAmount);
    this.appendFilter(filters, 'maxAmount', raw.maxAmount);
    this.appendFilter(filters, 'searchString', raw.searchString);
    this.appendFilter(filters, 'paymentTypeId', raw.paymentTypeId);
    this.appendFilter(filters, 'shopPointId', raw.shopPointId);
    this.appendFilter(filters, 'shopCompanyId', raw.shopCompanyId);
    this.appendFilter(filters, 'paymentTypeMode', raw.paymentTypeMode);
    this.appendFilter(filters, 'productPaymentMode', raw.productPaymentMode);

    if (raw.saleState !== null && raw.saleState !== undefined && String(raw.saleState) !== '-1') {
      filters.saleState = raw.saleState;
    }

    return filters;
  }

  normalizeResponse(response: unknown): SalesReportResponse {
    const source = this.parseBody(response);
    const record = this.toRecord(source);
    return {
      summary: this.normalizeSummary(this.toRecord(record['summary'])),
      sales: Array.isArray(record['sales']) ? record['sales'] as SalesReportSale[] : [],
      items: Array.isArray(record['items']) ? record['items'] as SalesReportItem[] : [],
      excelRows: Array.isArray(record['excelRows']) ? record['excelRows'] as SalesReportExcelRow[] : [],
      filters: {
        shopId: this.themingService.shopId,
        ...this.toRecord(record['filters']),
      } as SalesReportFilters,
    };
  }

  validateExcelRows(rows: SalesReportExcelRow[]): void {
    const validRows: ValidatedSalesReportExcelRow[] = [];
    const invalidRows: SalesReportExcelRow[] = [];

    rows.forEach((row) => {
      const normalized = this.normalizeExcelRow(row);
      if (this.isExportableRow(normalized)) {
        validRows.push(normalized);
      } else {
        invalidRows.push(row);
      }
    });

    this.validExcelRows = validRows;
    this.invalidExcelRows = invalidRows;
  }

  buildWorkbook(rows: ValidatedSalesReportExcelRow[]): XLSX.WorkBook {
    const worksheetRows = rows.map((row) => [
      row.identification,
      row.name,
      row.document ?? 0,
      this.parseLocalDate(row.date),
      row.principalAmount,
      row.discountAmount,
      row.netAmount,
      row.termMonths,
      this.parseLocalDate(row.firstInstallmentDate),
      row.monthlyInstallment,
      row.observations,
    ]);
    const worksheet = XLSX.utils.aoa_to_sheet([this.excelHeaders, ...worksheetRows], {
      cellDates: true,
    });

    worksheet['!cols'] = [
      { wch: 13 },
      { wch: 33 },
      { wch: 26 },
      { wch: 12 },
      { wch: 13 },
      { wch: 13 },
      { wch: 12 },
      { wch: 10 },
      { wch: 15 },
      { wch: 13 },
      { wch: 22 },
    ];

    for (let index = 0; index < rows.length; index++) {
      const excelRow = index + 2;
      this.setTextCell(worksheet, `A${excelRow}`, rows[index].identification);
      this.setDateFormat(worksheet, `D${excelRow}`);
      this.setDateFormat(worksheet, `I${excelRow}`);
      ['E', 'F', 'G', 'J'].forEach((column) => this.setMoneyFormat(worksheet, `${column}${excelRow}`));
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Machote Padron');
    return workbook;
  }

  buildFileName(): string {
    const raw = this.filtersForm.getRawValue();
    const start = this.safeFilePart(raw.startDate);
    const end = this.safeFilePart(raw.endDate);
    if (start && end) {
      return `Reporte_Ventas_${start}_${end}.xlsx`;
    }
    return `Reporte_Ventas_${this.getTodayIsoDate()}.xlsx`;
  }

  formatMoney(value: unknown): string {
    const amount = this.toNumber(value);
    const theme = this.themingService.shopTheming?.[0];
    try {
      return amount.toLocaleString(theme?.countryLanguage || 'es-CR', {
        style: 'currency',
        currency: theme?.currencyCode || 'CRC',
      });
    } catch {
      return amount.toFixed(2);
    }
  }

  formatDate(value: unknown): string {
    const date = this.toDisplayDate(value);
    return date || '-';
  }

  numberValue(value: unknown): number {
    return this.toNumber(value);
  }

  hasFieldError(fieldName: string, errorName: string): boolean {
    const field = this.filtersForm.get(fieldName);
    return !!field && field.touched && field.hasError(errorName);
  }

  hasFormError(errorName: string): boolean {
    return this.filtersForm.touched && this.filtersForm.hasError(errorName);
  }

  private async loadPaymentTypes(): Promise<void> {
    try {
      const response: any = await this.lambdaService.getPaymentMethods(this.themingService.shopId);
      const paymentTypes = Array.isArray(response?.paymentTypes) ? response.paymentTypes : [];
      this.paymentTypes = paymentTypes
        .filter((paymentType: any) => paymentType?.paymentTypeId !== undefined && paymentType?.paymentTypeName)
        .map((paymentType: any) => ({
          id: paymentType.paymentTypeId,
          name: String(paymentType.paymentTypeName),
        }));
    } catch (error) {
      console.error('Error loading report payment types:', error);
      this.paymentTypes = [];
    }
  }

  private async loadShopPoints(): Promise<void> {
    const themingShopPoints = this.themingService.shopTheming?.[0]?.shopPoints;
    if (Array.isArray(themingShopPoints) && themingShopPoints.length > 0) {
      this.shopPoints = this.mapShopPoints(themingShopPoints);
      return;
    }

    try {
      const response: any = await this.lambdaService.getShopPoints(this.themingService.shopId);
      this.shopPoints = this.mapShopPoints(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Error loading report shop points:', error);
      this.shopPoints = [];
    }
  }

  private async loadShopCompanies(): Promise<void> {
    try {
      const response: any = await this.lambdaService.getShopCompanies(this.themingService.shopId, true);
      const companies: ShopCompany[] = extractShopCompanies(response).filter((company) => company.status === 1);
      this.shopCompanies = companies.map((company) => ({
        id: company.shopCompanyId,
        name: company.companyName || company.companyLegalName || String(company.shopCompanyId),
      }));
    } catch (error) {
      console.error('Error loading report shop companies:', error);
      this.shopCompanies = [];
    }
  }

  private mapShopPoints(shopPoints: any[]): ReportCatalogOption[] {
    return shopPoints
      .filter((shopPoint) => shopPoint?.shopPointId !== undefined && shopPoint?.shopPointName)
      .map((shopPoint) => ({
        id: shopPoint.shopPointId,
        name: String(shopPoint.shopPointName),
      }));
  }

  private clearReportData(): void {
    this.reportResponse = null;
    this.summary = {};
    this.previewRows = [];
    this.validExcelRows = [];
    this.invalidExcelRows = [];
  }

  private appendFilter(filters: SalesReportFilters, key: keyof SalesReportFilters, value: unknown): void {
    if (value === null || value === undefined || String(value).trim() === '') {
      return;
    }
    (filters as unknown as Record<string, unknown>)[key] = value;
  }

  private normalizeSummary(summary: Record<string, unknown>): SalesReportSummary {
    return {
      ...summary,
      totalSales: this.toNumber(summary['totalSales']),
      totalClients: this.toNumber(summary['totalClients']),
      totalAssociations: this.toNumber(summary['totalAssociations']),
      totalPrincipalAmount: this.toNumber(summary['totalPrincipalAmount']),
      totalDiscountAmount: this.toNumber(summary['totalDiscountAmount']),
      totalNetAmount: this.toNumber(summary['totalNetAmount']),
      totalMonthlyInstallment: this.toNumber(summary['totalMonthlyInstallment']),
      totalPaidAmount: this.toNumber(summary['totalPaidAmount']),
      calculatedPendingBalance: this.toNumber(summary['calculatedPendingBalance']),
      totalPaymentsCount: this.toNumber(summary['totalPaymentsCount']),
    };
  }

  private toPreviewRow(sale: SalesReportSale): SalesReportPreviewRow {
    const saleRecord = sale as Record<string, unknown>;
    const orderCode = this.firstString(saleRecord, ['saleOrderCode', 'orderCode', 'order', 'saleShortCode']);
    return {
      identification: this.firstString(saleRecord, ['identification', 'clientNationalId', 'nationalId']),
      client: this.firstStringFromRecords([
        { record: saleRecord, keys: ['fullName', 'clientFullName', 'clientName', 'name'] },
      ]),
      order: orderCode ? orderCode.slice(-8) : '-',
      date: this.firstString(saleRecord, ['saleDate', 'date']),
      association: this.firstString(saleRecord, ['association', 'shopCompanyName', 'companyName']),
      principalAmount: this.toNumber(this.firstValue(saleRecord, ['principalAmount', 'saleEndPrice', 'netAmount'])),
      termMonths: this.toNullableNumber(this.firstValue(saleRecord, ['termMonths', 'term'])),
      firstInstallmentDate: this.firstString(saleRecord, ['firstInstallmentDate']),
      monthlyInstallment: this.toNumber(this.firstValue(saleRecord, ['monthlyInstallment'])),
      observations: this.firstStringFromRecords([
        { record: saleRecord, keys: ['reportObservations', 'observations', 'products', 'productNames', 'productName'] },
      ]),
    };
  }

  private normalizeExcelRow(row: SalesReportExcelRow): ValidatedSalesReportExcelRow {
    const principalAmount = this.toNumber(row.principalAmount);
    const termMonths = this.toNumber(row.termMonths);
    const fallbackMonthly = termMonths > 0 ? principalAmount / termMonths : 0;
    return {
      ...row,
      identification: String(row.identification ?? '').trim(),
      name: this.normalizeWhitespace(row.name),
      document: row.document ?? 0,
      date: String(row.date ?? '').trim(),
      principalAmount,
      discountAmount: this.toNumber(row.discountAmount),
      netAmount: this.toNumber(row.netAmount || principalAmount),
      termMonths,
      firstInstallmentDate: String(row.firstInstallmentDate ?? '').trim(),
      monthlyInstallment: this.toNumber(row.monthlyInstallment || fallbackMonthly),
      observations: this.normalizeWhitespace(row.observations),
    };
  }

  private isExportableRow(row: ValidatedSalesReportExcelRow): boolean {
    return !!row.identification
      && !!row.name
      && !!row.date
      && Number.isFinite(row.principalAmount)
      && row.principalAmount > 0
      && !!row.firstInstallmentDate
      && Number.isFinite(row.termMonths)
      && row.termMonths > 0;
  }

  private parseLocalDate(value: string): Date | string {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (!match) {
      return value;
    }
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }

  private setTextCell(worksheet: XLSX.WorkSheet, address: string, value: string): void {
    worksheet[address] = {
      t: 's',
      v: value,
      z: '@',
    } as ExcelCell;
  }

  private setDateFormat(worksheet: XLSX.WorkSheet, address: string): void {
    const cell = worksheet[address] as ExcelCell | undefined;
    if (cell) {
      cell.z = 'dd/mm/yyyy';
    }
  }

  private setMoneyFormat(worksheet: XLSX.WorkSheet, address: string): void {
    const cell = worksheet[address] as ExcelCell | undefined;
    if (cell) {
      cell.z = '#,##0.00';
    }
  }

  private nonNegativeNumberValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value === null || value === undefined || value === '') {
        return null;
      }
      const numberValue = Number(value);
      if (!Number.isFinite(numberValue)) {
        return { number: true };
      }
      return numberValue < 0 ? { nonNegative: true } : null;
    };
  }

  private dateRangeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const start = control.get('startDate')?.value;
      const end = control.get('endDate')?.value;
      if (!start || !end) {
        return null;
      }
      return String(start) > String(end) ? { dateRange: true } : null;
    };
  }

  private amountRangeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const min = control.get('minAmount')?.value;
      const max = control.get('maxAmount')?.value;
      if (min === null || min === undefined || min === '' || max === null || max === undefined || max === '') {
        return null;
      }
      return Number(min) > Number(max) ? { amountRange: true } : null;
    };
  }

  private toNumber(value: unknown): number {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : 0;
    }
    if (typeof value === 'string') {
      const normalized = value.replace(/,/g, '').trim();
      if (!normalized) {
        return 0;
      }
      const parsed = Number(normalized);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
  }

  private toNullableNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    return this.toNumber(value);
  }

  private firstValue(record: Record<string, unknown>, keys: string[]): unknown {
    return keys.map((key) => record[key]).find((value) => value !== null && value !== undefined && String(value) !== '');
  }

  private firstString(record: Record<string, unknown>, keys: string[]): string {
    const value = this.firstValue(record, keys);
    return value === null || value === undefined || String(value).trim() === '' ? '-' : String(value).trim();
  }

  private firstStringFromRecords(sources: Array<{ record: Record<string, unknown>; keys: string[] }>): string {
    for (const source of sources) {
      const value = this.firstValue(source.record, source.keys);
      if (value !== null && value !== undefined && String(value).trim() !== '') {
        return String(value).trim();
      }
    }

    return '-';
  }

  private normalizeWhitespace(value: unknown): string {
    return String(value ?? '').replace(/\s+/g, ' ').trim();
  }

  private parseBody(response: unknown): unknown {
    const record = this.toRecord(response);
    if (typeof record['body'] === 'string') {
      try {
        return JSON.parse(record['body']);
      } catch {
        return response;
      }
    }
    return response;
  }

  private toRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' ? value as Record<string, unknown> : {};
  }

  private toDisplayDate(value: unknown): string {
    if (!value) {
      return '';
    }
    const text = String(value);
    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
    if (isoMatch) {
      return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
    }
    const date = new Date(text);
    if (Number.isNaN(date.getTime())) {
      return text;
    }
    return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
  }

  private safeFilePart(value: unknown): string {
    return String(value || '').replace(/[^0-9A-Za-z_-]/g, '');
  }

  private getTodayIsoDate(): string {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  }
}
